import { useCallback, useEffect } from 'react';
import { store } from '../state/store';
import { cleanupDrivingMode } from '../components/drivingMode/DrivingMode';
import type { McpChatWidgetProps } from '../interfaces/mcpChatWidgetProps';
import type { McpWidgetRuntimeRefs } from './types';
import {
  setOrchestratorRecoveryHandler,
  setOrchestratorRef,
} from '../components/messageList/MessageList.helpers';
import { McpClient } from '../services/mcp-client';
import { ChatOrchestrator } from '../services/orchestrator';

interface UseWidgetSessionRuntimeArgs {
  props: McpChatWidgetProps;
  shadow: ShadowRoot;
  refs: McpWidgetRuntimeRefs;
  closeMcpSession(): Promise<void>;
  fetchAnonymousToken(): Promise<void>;
}

export function useWidgetSessionRuntime({
  props,
  shadow,
  refs,
  closeMcpSession,
  fetchAnonymousToken,
}: UseWidgetSessionRuntimeArgs) {
  const resetDrivingModeState = useCallback((): void => {
    cleanupDrivingMode();
    store.setDrivingMode({
      active: false,
      listening: false,
      speaking: false,
      processing: false,
      transcript: '',
      lastResponse: '',
      error: null,
      keypadOpen: false,
      userGender: undefined,
    });
  }, []);

  const initMcpSession = useCallback(async (): Promise<void> => {
    if (refs.mcpReadyPromiseRef.current) {
      await refs.mcpReadyPromiseRef.current;
      return;
    }

    let resolveReady!: () => void;
    const readyPromise = new Promise<void>((resolve) => {
      resolveReady = resolve;
    });
    refs.mcpReadyPromiseRef.current = readyPromise;

    try {
      if (refs.healthCheckPromiseRef.current) {
        await refs.healthCheckPromiseRef.current;
      }

      if (store.getState().auth.status !== 'authenticated') {
        refs.mcpReadyPromiseRef.current = null;
        return;
      }

      const mcpClient = new McpClient();
      refs.mcpClientRef.current = mcpClient;

      const orchestrator = new ChatOrchestrator(mcpClient, shadow);
      refs.orchestratorRef.current = orchestrator;
      setOrchestratorRef(orchestrator);
      orchestrator.setMcpReadyPromise(readyPromise);

      if (refs.anonymousTokenPromiseRef.current) {
        await refs.anonymousTokenPromiseRef.current;
      }

      await mcpClient.initialize();
      await mcpClient.listTools();

    } catch (err) {
      console.warn('[mcp-chat-widget] MCP init failed, chat will work without tools:', err);
    } finally {
      resolveReady();
    }
  }, [
    refs.anonymousTokenPromiseRef,
    refs.healthCheckPromiseRef,
    refs.mcpClientRef,
    refs.mcpReadyPromiseRef,
    refs.orchestratorRef,
    shadow,
  ]);

  useEffect(() => {
    setOrchestratorRecoveryHandler(async () => {
      await initMcpSession();
      return refs.orchestratorRef.current;
    });

    return () => {
      setOrchestratorRecoveryHandler(null);
    };
  }, [initMcpSession, refs.orchestratorRef]);

  const handleSessionExpiry = useCallback((): void => {
    const { config } = store.getState();

    resetDrivingModeState();

    store.setState({
      messages: [],
      conversationId: null,
      tools: [],
      toolCalls: [],
      isLoading: false,
      error: null,
    });
    void closeMcpSession();

    if (config.auth === 'required') {
      store.setAuth({ status: 'unauthenticated', email: null, token: null });
      return;
    }

    store.setAuth({ status: 'authenticated', email: null, token: null });
    if (!props.preview) {
      refs.anonymousTokenPromiseRef.current = fetchAnonymousToken();
    }
  }, [closeMcpSession, fetchAnonymousToken, props.preview, refs.anonymousTokenPromiseRef, resetDrivingModeState]);

  const resetTransientState = useCallback((): void => {
    refs.anonymousTokenPromiseRef.current = null;
    refs.healthCheckPromiseRef.current = null;
    refs.mcpReadyPromiseRef.current = null;
  }, [
    refs.anonymousTokenPromiseRef,
    refs.healthCheckPromiseRef,
    refs.mcpReadyPromiseRef,
  ]);

  const toggleDrivingMode = useCallback(async (): Promise<void> => {
    if (!props.portalMode) {
      resetDrivingModeState();
      return;
    }

    const active = store.getState().drivingMode.active;
    if (active) {
      resetDrivingModeState();
      return;
    }

    store.setDrivingMode({ processing: true, error: null });
    await initMcpSession();

    if (!refs.orchestratorRef.current) {
      store.setDrivingMode({ processing: false, error: 'sttNetworkError' });
      return;
    }

    store.setDrivingMode({ active: true, processing: false, error: null });
  }, [initMcpSession, props.portalMode, refs.orchestratorRef, resetDrivingModeState]);

  const clearServerAuthOverride = useCallback((): void => {
    store.setConfig({ authProvider: 'none' });
  }, []);

  const getMcpReadyPromise = useCallback((): Promise<void> | null => refs.mcpReadyPromiseRef.current, [
    refs.mcpReadyPromiseRef,
  ]);

  return {
    orchestratorRef: refs.orchestratorRef,
    getMcpReadyPromise,
    initMcpSession,
    handleSessionExpiry,
    closeMcpSession,
    resetTransientState,
    toggleDrivingMode,
    clearServerAuthOverride,
  };
}
